import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, BookOpen, FileText, Sparkles, ArrowLeft, Eye, Trash2, Send, CheckCircle, Users, Plus, ClipboardList, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import StudentInsights from '@/components/StudentInsights';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_TYPES = [
  { id: 'unit', label: 'Unit Test' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'half_yearly', label: 'Half-Yearly' },
  { id: 'annual', label: 'Annual' },
];

const SCERT_SUBJECTS = ['Mathematics', 'Science', 'Social Studies', 'English', 'Telugu'];
const NCERT_SUBJECTS = ['Mathematics', 'Science', 'Social Studies', 'English'];

const TeacherMockExams = ({ teacherInfo }) => {
  const [view, setView] = useState('list'); // list | coverage | exam-detail | attempts
  const [selectedClass, setSelectedClass] = useState((teacherInfo?.class_levels?.[0]) || '6');
  const [selectedSubject, setSelectedSubject] = useState((teacherInfo?.subjects?.[0]) || 'Mathematics');
  const [subjects, setSubjects] = useState(SCERT_SUBJECTS);
  const [mockExams, setMockExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMockId, setSelectedMockId] = useState(null);

  useEffect(() => {
    // Adjust subject list based on teacher's subjects, else default
    const teacherSubs = teacherInfo?.subjects;
    if (teacherSubs && teacherSubs.length > 0) {
      setSubjects(teacherSubs);
      if (!teacherSubs.includes(selectedSubject)) {
        setSelectedSubject(teacherSubs[0]);
      }
    } else {
      setSubjects(SCERT_SUBJECTS);
    }
  }, [teacherInfo]);

  useEffect(() => {
    loadMockExams();
  }, [selectedClass, selectedSubject]);

  const loadMockExams = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/teacher/mock-exams`, {
        params: { class_level: selectedClass, subject: selectedSubject },
      });
      setMockExams(res.data.mock_exams || []);
    } catch (err) {
      console.error('Error loading mock exams', err);
      toast.error('Failed to load mock exams');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'coverage') {
    return (
      <CoverageEditor
        classLevel={selectedClass}
        subject={selectedSubject}
        onBack={() => { setView('list'); loadMockExams(); }}
      />
    );
  }
  if (view === 'exam-detail' && selectedMockId) {
    return (
      <MockExamDetail
        mockId={selectedMockId}
        onBack={() => { setSelectedMockId(null); setView('list'); loadMockExams(); }}
        onViewAttempts={() => setView('attempts')}
      />
    );
  }
  if (view === 'attempts' && selectedMockId) {
    return (
      <MockExamAttempts
        mockId={selectedMockId}
        onBack={() => setView('exam-detail')}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
              <ClipboardList className="text-blue-600" size={22} />
              Class Exams
            </h3>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              Mark chapters covered → Generate full-format paper → Publish for students. <span className="font-medium text-blue-600">For student practice only — not used for report cards.</span>
            </p>
          </div>
          <Button
            onClick={() => setView('coverage')}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="mark-coverage-btn"
          >
            <BookOpen size={16} className="mr-2" />
            Mark Chapter Coverage
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="mock-class-select"
            >
              {['6', '7', '8', '9', '10'].map(c => <option key={c} value={c}>Class {c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="mock-subject-select"
            >
              {subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Quick Generate Buttons */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 md:p-6 border-2 border-dashed border-purple-200">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Sparkles className="text-purple-600" size={18} />
          Generate Mock Paper
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {EXAM_TYPES.map(et => (
            <GenerateButton
              key={et.id}
              examType={et.id}
              label={et.label}
              classLevel={selectedClass}
              subject={selectedSubject}
              onSuccess={loadMockExams}
            />
          ))}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b">
          <h4 className="font-semibold text-gray-800">My Mock Papers ({mockExams.length})</h4>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2 className="mx-auto mb-2 animate-spin" />
            Loading...
          </div>
        ) : mockExams.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="mx-auto mb-2 text-gray-300" size={48} />
            <p>No mock exams yet for Class {selectedClass} {selectedSubject}</p>
            <p className="text-xs mt-1">Mark chapter coverage above and click "Generate" to create one.</p>
          </div>
        ) : (
          <div className="divide-y">
            {mockExams.map(m => (
              <div
                key={m.id}
                className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-3"
                onClick={() => { setSelectedMockId(m.id); setView('exam-detail'); }}
                data-testid={`mock-exam-row-${m.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant={m.status === 'published' ? 'default' : 'outline'}>
                      {m.status === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                    <Badge variant="secondary">{m.board}</Badge>
                    <Badge variant="outline">{m.total_marks} marks</Badge>
                  </div>
                  <p className="font-medium text-gray-800 text-sm truncate">{m.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {m.duration_minutes} min · {new Date(m.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Eye className="text-gray-400" size={18} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== GENERATE BUTTON ==============
const GenerateButton = ({ examType, label, classLevel, subject, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    if (!window.confirm(`Generate ${label} mock paper for Class ${classLevel} ${subject}?\n\nMake sure you have marked chapter coverage first.`)) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/teacher/mock-exams/generate`, {
        class_level: classLevel, subject, exam_type: examType,
      });
      const shortfalls = res.data.shortfalls || [];
      if (shortfalls.length > 0) {
        toast.warning(`Generated with shortfalls: ${shortfalls.map(s => `${s.section} (${s.available}/${s.wanted})`).join(', ')}`);
      } else {
        toast.success(`${label} mock paper generated!`);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      variant="outline"
      onClick={generate}
      disabled={loading}
      className="border-purple-300 hover:bg-purple-100 text-purple-800 text-xs md:text-sm"
      data-testid={`generate-${examType}-btn`}
    >
      {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
      {label}
    </Button>
  );
};

// ============== COVERAGE EDITOR ==============
const CoverageEditor = ({ classLevel, subject, onBack }) => {
  const [chapters, setChapters] = useState([]);
  const [coverage, setCoverage] = useState({}); // exam_type -> Set(chapter_ids)
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // exam_type being saved

  useEffect(() => { loadData(); }, [classLevel, subject]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sylRes, covRes] = await Promise.all([
        axios.get(`${API}/teacher/syllabus`, { params: { class_level: classLevel, subject } }),
        axios.get(`${API}/teacher/mock-exam/coverage`, { params: { class_level: classLevel, subject } }),
      ]);
      setChapters(sylRes.data.chapters || []);
      const cov = covRes.data.coverage || {};
      const covSets = {};
      Object.entries(cov).forEach(([k, v]) => { covSets[k] = new Set(v || []); });
      setCoverage(covSets);
      setMeta(covRes.data.meta || {});
    } catch (err) {
      console.error(err);
      toast.error('Failed to load syllabus / coverage');
    } finally { setLoading(false); }
  };

  const toggle = (examType, chapterId) => {
    setCoverage(prev => {
      const next = { ...prev };
      const set = new Set(next[examType] || []);
      if (set.has(chapterId)) set.delete(chapterId);
      else set.add(chapterId);
      next[examType] = set;
      return next;
    });
  };

  const save = async (examType) => {
    try {
      setSaving(examType);
      const chapter_ids = Array.from(coverage[examType] || []);
      await axios.post(`${API}/teacher/mock-exam/coverage`, {
        class_level: classLevel, subject, exam_type: examType, chapter_ids,
      });
      toast.success(`Saved ${EXAM_TYPES.find(e => e.id === examType)?.label} coverage (${chapter_ids.length} chapters)`);
      loadData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="back-from-coverage">
        <ArrowLeft size={16} className="mr-1" /> Back
      </Button>

      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-1">Chapter Coverage</h3>
        <p className="text-sm text-gray-500">
          Class {classLevel} · {subject} — Mark which chapters are completed for each exam type. The mock paper will only use questions from chapters you have ticked.
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center"><Loader2 className="mx-auto animate-spin text-blue-500" /></div>
      ) : (
        EXAM_TYPES.map(et => (
          <div key={et.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b">
              <div>
                <h4 className="font-semibold text-gray-800">{et.label}</h4>
                <p className="text-xs text-gray-500">
                  {(coverage[et.id]?.size || 0)} / {chapters.length} chapters selected
                  {meta[et.id]?.updated_at && ` · last saved ${new Date(meta[et.id].updated_at).toLocaleDateString()}`}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => save(et.id)}
                disabled={saving === et.id}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid={`save-coverage-${et.id}`}
              >
                {saving === et.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                Save
              </Button>
            </div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
              {chapters.map(ch => {
                const checked = coverage[et.id]?.has(ch.id) || false;
                return (
                  <label
                    key={ch.id}
                    className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer border ${checked ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(et.id, ch.id)}
                      className="mt-0.5"
                      data-testid={`coverage-${et.id}-${ch.id}`}
                    />
                    <span className="text-sm flex-1">
                      <span className="font-medium">Ch {ch.order}: {ch.name}</span>
                      {ch.questions_generated && <Badge variant="outline" className="ml-2 text-xs">{ch.question_count}Q</Badge>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ============== MOCK DETAIL ==============
const MockExamDetail = ({ mockId, onBack, onViewAttempts }) => {
  const [mock, setMock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [mockId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/teacher/mock-exams/${mockId}`);
      setMock(res.data);
    } catch (err) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const publish = async () => {
    try {
      await axios.patch(`${API}/teacher/mock-exams/${mockId}/publish`);
      toast.success('Published! Students can now see this exam.');
      load();
    } catch (err) { toast.error('Failed to publish'); }
  };
  const unpublish = async () => {
    try {
      await axios.patch(`${API}/teacher/mock-exams/${mockId}/unpublish`);
      toast.success('Unpublished');
      load();
    } catch (err) { toast.error('Failed'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this mock exam and all attempts? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/teacher/mock-exams/${mockId}`);
      toast.success('Deleted');
      onBack();
    } catch (err) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!mock) return null;

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft size={16} className="mr-1" /> Back</Button>

      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-gray-800">{mock.title}</h3>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              {mock.board} · {mock.total_marks} marks · {mock.duration_minutes} min · {mock.chapter_ids?.length || 0} chapters
            </p>
          </div>
          <Badge variant={mock.status === 'published' ? 'default' : 'outline'}>
            {mock.status === 'published' ? 'Published' : 'Draft'}
          </Badge>
        </div>

        {mock.shortfalls && mock.shortfalls.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3 text-xs text-yellow-800">
            <strong>Note:</strong> Some sections have fewer questions than the pattern requires.
            {mock.shortfalls.map((s, i) => (
              <div key={i}>· {s.section}: {s.available}/{s.wanted}</div>
            ))}
            <div className="mt-1">Generate question banks for more chapters in <em>Syllabus</em> to fill these gaps.</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {mock.status === 'draft' ? (
            <Button onClick={publish} className="bg-green-600 hover:bg-green-700" data-testid="publish-mock-btn">
              <Send size={16} className="mr-2" /> Publish to Students
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={unpublish} data-testid="unpublish-mock-btn">Unpublish</Button>
              <Button variant="outline" onClick={onViewAttempts} data-testid="view-attempts-btn">
                <Users size={16} className="mr-1" /> View Attempts
              </Button>
            </>
          )}
          <Button variant="destructive" onClick={remove} data-testid="delete-mock-btn">
            <Trash2 size={16} className="mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Sections */}
      {mock.sections?.map((sec, idx) => (
        <div key={idx} className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-800">{sec.name}</h4>
              <p className="text-xs text-gray-500">{sec.actual_count} questions · {sec.marks_per_question} mark{sec.marks_per_question > 1 ? 's' : ''} each</p>
            </div>
            <Badge variant="outline">{sec.actual_count * sec.marks_per_question} marks</Badge>
          </div>
          <div className="p-3 space-y-3 max-h-96 overflow-y-auto">
            {(sec.questions || []).map((q, qi) => (
              <div key={q.id || qi} className="border rounded-lg p-3 bg-gray-50">
                <p className="text-sm font-medium mb-1">Q{q.question_number}. {q.question}</p>
                {sec.type === 'mcq' && q.options && (
                  <div className="ml-3 text-xs text-gray-600 space-y-0.5">
                    {Object.entries(q.options).map(([k, v]) => (
                      <div key={k} className={q.correct === k ? 'font-semibold text-green-700' : ''}>
                        {k}. {v} {q.correct === k && '✓'}
                      </div>
                    ))}
                  </div>
                )}
                {(sec.type === 'short_answer' || sec.type === 'descriptive') && (
                  <p className="ml-3 text-xs text-gray-600 italic mt-1">
                    <strong>Model:</strong> {q.model_answer || '—'}
                  </p>
                )}
                {q.chapter_name && <p className="text-xs text-gray-400 mt-1">From: {q.chapter_name}</p>}
              </div>
            ))}
            {(sec.questions || []).length === 0 && (
              <p className="text-xs text-gray-400 italic">No questions available — generate question banks for the covered chapters.</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== MOCK EXAM ATTEMPTS ==============
const MockExamAttempts = ({ mockId, onBack }) => {
  const [data, setData] = useState({ mock_exam: null, attempts: [] });
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  useEffect(() => { load(); }, [mockId]);
  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/teacher/mock-exams/${mockId}/attempts`);
      setData(res.data);
    } catch (err) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  const m = data.mock_exam;

  if (selectedAttempt) {
    return <AttemptReview attemptId={selectedAttempt} mock={m} onBack={() => setSelectedAttempt(null)} />;
  }

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft size={16} className="mr-1" /> Back</Button>
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h3 className="font-bold">{m?.title}</h3>
        <p className="text-xs text-gray-500">{data.attempts.length} student(s) submitted</p>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {data.attempts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No submissions yet.</div>
        ) : (
          <div className="divide-y">
            {data.attempts.map(a => {
              const pct = a.percentage || (m?.total_marks ? (a.auto_score / m.total_marks * 100).toFixed(1) : 0);
              return (
                <div key={a.id} className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50 cursor-pointer"
                     onClick={() => setSelectedAttempt(a.id)}
                     data-testid={`attempt-row-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{a.student_name}</p>
                    <p className="text-xs text-gray-500">
                      Submitted {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-600">{a.auto_score} / {m?.total_marks}</p>
                    <p className="text-xs text-gray-500">{pct}%</p>
                    {a.manual_review_pending && (
                      <Badge variant="outline" className="text-xs text-orange-600 mt-1">Needs review</Badge>
                    )}
                  </div>
                  <Eye size={18} className="text-gray-400 shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== PER-STUDENT ATTEMPT REVIEW ==============
const AttemptReview = ({ attemptId, mock, onBack }) => {
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('insights'); // 'insights' | 'details'

  useEffect(() => { load(); }, [attemptId]);
  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/teacher/mock-exam-attempts/${attemptId}`);
      setAttempt(res.data);
    } catch (err) { toast.error('Failed to load attempt'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!attempt) return null;

  const results = attempt.per_question_results || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border">
          <Button
            size="sm"
            variant={view === 'insights' ? 'default' : 'ghost'}
            onClick={() => setView('insights')}
            data-testid="tab-insights"
          >
            <Sparkles size={14} className="mr-1" /> Insights
          </Button>
          <Button
            size="sm"
            variant={view === 'details' ? 'default' : 'ghost'}
            onClick={() => setView('details')}
            data-testid="tab-details"
          >
            Question Details
          </Button>
        </div>
      </div>

      {view === 'insights' ? (
        <StudentInsights
          diagnosticUrl={`${API}/teacher/mock-exam-attempts/${attemptId}/diagnostic`}
          actionPlanUrl={`${API}/teacher/mock-exam-attempts/${attemptId}/ai-action-plan`}
          onShowDetails={() => setView('details')}
        />
      ) : (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-4 shadow-sm">
            <p className="text-xs uppercase opacity-80">Question-by-Question Review</p>
            <h3 className="font-bold">{attempt.student_name} · {mock?.title}</h3>
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {results.map((r, i) => {
                const cls = r.status === 'correct' ? 'bg-green-50 border-l-green-500'
                  : r.status === 'partial' ? 'bg-yellow-50 border-l-yellow-500'
                  : r.status === 'incorrect' ? 'bg-red-50 border-l-red-500'
                  : r.status === 'needs_review' ? 'bg-blue-50 border-l-blue-500'
                  : 'bg-gray-50 border-l-gray-300';
                return (
                  <div key={i} className={`p-3 border-l-4 ${cls}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold flex-1">Q{i + 1}. {r.question}</p>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {r.awarded} / {r.marks}
                      </Badge>
                    </div>

                    <div className="space-y-2 ml-2">
                      {r.type === 'mcq' && r.options && Object.keys(r.options).length > 0 ? (
                        <div className="space-y-1">
                          {Object.entries(r.options).map(([letter, text]) => {
                            const isRight = letter === r.correct;
                            const isPick = letter === r.user_answer;
                            const optCls = isRight
                              ? 'bg-green-100 border-green-300 text-green-900'
                              : isPick
                                ? 'bg-red-100 border-red-300 text-red-900'
                                : 'bg-white border-gray-200 text-gray-700';
                            return (
                              <div key={letter} className={`flex items-start gap-2 p-2 rounded border text-xs ${optCls}`}>
                                <span className="font-semibold">{letter}.</span>
                                <span className="flex-1">{text}</span>
                                {isRight && <span className="text-green-700 font-medium shrink-0">✓ Correct</span>}
                                {isPick && !isRight && <span className="text-red-700 font-medium shrink-0">✗ Student picked</span>}
                              </div>
                            );
                          })}
                          {!r.user_answer && (
                            <p className="text-xs text-amber-700 italic">Student did not answer.</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">Student's answer:</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {r.user_answer || <em className="text-gray-400">Not answered</em>}
                            </p>
                          </div>
                          {(r.correct || r.model_answer) && (
                            <div className="bg-green-50 border border-green-200 rounded p-2">
                              <p className="text-xs font-semibold text-green-700 mb-1">
                                {r.type === 'mcq' ? 'Correct answer:' : 'Model answer:'}
                              </p>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                {r.correct || r.model_answer}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {r.status === 'needs_review' && (
                        <p className="text-xs text-blue-700">
                          <AlertCircle size={12} className="inline mr-1" />
                          Awaiting your manual review.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeacherMockExams;
